/**
 * CanvasLocaleSelector - Locale switcher for canvas screen node previews
 *
 * Shows available locales in a popover. Switching locale updates all
 * screen node previews via CanvasLocaleContext. Session-only (resets on reopen).
 */
import React, { useState, useRef } from 'react';
import {
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Badge,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '@mui/material/styles';

interface CanvasLocaleSelectorProps {
  locales: string[];
  currentLocale: string;
  defaultLocale: string;
  onLocaleChange: (locale: string) => void;
}

const CanvasLocaleSelector: React.FC<CanvasLocaleSelectorProps> = ({
  locales,
  currentLocale,
  defaultLocale,
  onLocaleChange,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const isNonDefault = currentLocale !== defaultLocale;

  const handleSelect = (locale: string) => {
    onLocaleChange(locale);
    setOpen(false);
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={() => setOpen(!open)}
        size="small"
        data-testid="canvas-locale-btn"
        sx={{
          bgcolor: 'background.paper',
          boxShadow: theme.shadows[2],
          '&:hover': { bgcolor: 'action.hover' },
          width: 36,
          height: 36,
          ...(isNonDefault && {
            color: 'primary.main',
            border: `1px solid`,
            borderColor: 'primary.main',
          }),
        }}
      >
        <Badge
          color="primary"
          variant="dot"
          invisible={!isNonDefault}
        >
          <TranslateIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        data-testid="canvas-locale-popover"
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 180,
              boxShadow: theme.shadows[4],
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            PREVIEW LANGUAGE
          </Typography>
        </Box>
        <List dense sx={{ pt: 0 }} data-testid="canvas-locale-list">
          {locales.map((locale) => {
            const isSelected = locale === currentLocale;
            const isDefault = locale === defaultLocale;
            return (
              <ListItemButton
                key={locale}
                selected={isSelected}
                onClick={() => handleSelect(locale)}
                sx={{ px: 2 }}
                data-testid={`canvas-locale-item-${locale}`}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {isSelected ? (
                    <CheckIcon fontSize="small" color="primary" />
                  ) : (
                    <Box sx={{ width: 20 }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      {locale}
                      {isDefault && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          (default)
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
    </>
  );
};

export default CanvasLocaleSelector;
